// Drawola Studio: the list of shareable links a draw has produced. Fork-only.
//
// Deliberately hook-free: the same list has to appear in the Studio (which
// renders outside the `<Excalidraw>` tree and cannot use upstream's `Dialog`)
// and later in the editor (which can). So this component takes its data and its
// translator as props, and each caller wraps it in the right shell. The split
// mirrors `storage/ui/StorageMeter.tsx`.

import React from "react";

import { formatRelativeTime } from "./format";

import type { DocShare } from "../storage/types";
import type { StudioTranslate } from "./i18n";

type Props = {
  shares: DocShare[];
  t: StudioTranslate;
  onCopy: (share: DocShare) => void;
  onRevoke: (share: DocShare) => void;
  /** Id of the share whose revoke is in flight; its actions stay disabled. */
  busyShareId: string | null;
  /** Id of the share whose link was just copied; its copy button says so. */
  copiedShareId: string | null;
};

export const SharesListView: React.FC<Props> = ({
  shares,
  t,
  onCopy,
  onRevoke,
  busyShareId,
  copiedShareId,
}) => {
  if (shares.length === 0) {
    return <p className="drawola-studio__shares-empty">{t("shares.empty")}</p>;
  }

  return (
    <ul className="drawola-studio__shares">
      {shares.map((share) => {
        // A share whose `expiresAt` is in the past is already gone from the
        // server: the cleanup job has deleted the objects, so the local record
        // is only a leftover. Offer "clear" instead of "revoke": there is
        // nothing left to revoke, only the record to drop.
        const expired = share.expiresAt <= Date.now();
        return (
          <li
            key={share.id}
            className={
              expired
                ? "drawola-studio__share is-expired"
                : "drawola-studio__share"
            }
          >
            <div className="drawola-studio__share-meta">
              <span className="drawola-studio__share-created">
                {t("shares.created", {
                  time: formatRelativeTime(share.createdAt, t),
                })}
              </span>
              <span className="drawola-studio__share-expiry">
                {expired
                  ? t("shares.expired", {
                      date: new Date(share.expiresAt).toLocaleDateString(),
                    })
                  : t("shares.expiresOn", {
                      date: new Date(share.expiresAt).toLocaleDateString(),
                    })}
              </span>
            </div>
            <div className="drawola-studio__share-actions">
              <button
                type="button"
                className="drawola-studio__share-action"
                onClick={() => onCopy(share)}
                disabled={busyShareId === share.id}
              >
                {copiedShareId === share.id
                  ? t("shares.copied")
                  : t("shares.copy")}
              </button>
              <button
                type="button"
                className={
                  expired
                    ? "drawola-studio__share-action"
                    : "drawola-studio__share-action drawola-studio__share-action--danger"
                }
                onClick={() => onRevoke(share)}
                disabled={busyShareId === share.id}
              >
                {expired ? t("shares.clear") : t("shares.revoke")}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
